"use client";

import clientSide from "@/helpers/clientSide";
import { useAppContext } from "@/context/AppContext";
import { waitForCohesion } from "@/lib/cohesion";
import { setPageAction, trackError } from "@/lib/newrelic";
import waitFor from "@/lib/wait-for";
import { cohesion, deferredPromise } from "@redventures/cohesion-utils-react";
import { usePathname } from "next/navigation";
import Script from "next/script";
import { useEffect, useRef } from "react";
import { getPageName, getPrimaryCategory, getScreenName } from "./utils";

const cohesionPromise = deferredPromise();
const layoutPromise = deferredPromise();

export default function AdobeDataLayer() {
  const isProduction = process.env.NEXT_PUBLIC_APP_ENV === "production";
  const url = isProduction
    ? "https://static.cimcontent.net/data-layer/?appID=resi_global"
    : "https://static.cimcontent.net/data-layer/nonprod?appID=resi_global";
  const pathname = usePathname();
  const resolvedPathname =
    pathname ?? (clientSide ? window.location.pathname : null);
  const screenName = getScreenName(resolvedPathname);
  const primaryCategory = getPrimaryCategory(resolvedPathname);
  const pageName = getPageName(resolvedPathname);
  const { isLayoutReady } = useAppContext();
  const isLayoutReadyRef = useRef(isLayoutReady);
  const isTrackingReadyRef = useRef(false);
  const previousScreenNameRef = useRef(screenName);
  const screenNameRef = useRef(screenName);

  useEffect(() => {
    isLayoutReadyRef.current = isLayoutReady;
  }, [isLayoutReady]);

  useEffect(() => {
    screenNameRef.current = screenName;
  }, [screenName]);

  const handlePageView = (currentScreenName: string) => {
    const detail = {
      screenName: currentScreenName,
      siteInfo: {
        rvAnonymousId: window?._Cohesion?.anonymousId || "NA",
        rvInstanceId: window?._Cohesion?.instanceId || "NA",
        rvSessionId: window?._Cohesion?.sessionId || "NA",
      },
    };
    document.dispatchEvent(
      new CustomEvent("c-tracking-log-page", {
        bubbles: true,
        detail,
      })
    );
  };

  useEffect(() => {
    const abortController = new AbortController();

    Promise.all([cohesionPromise, layoutPromise])
      .then(() => {
        cohesion("ready", () => {
          isTrackingReadyRef.current = true;
          handlePageView(screenNameRef.current);
          setPageAction("adobeDataLayerLoaded");
          setPageAction("cohesionReady");
        });
      })
      .catch((error) => {
        isTrackingReadyRef.current = true;
        handlePageView(screenNameRef.current);
        setPageAction("adobeDataLayerLoadedManually");
        trackError(error);
      });

    waitFor(() => isLayoutReadyRef.current, {
      errorMessage: "Layout failed to load",
      timeoutMs: 10000,
      pollIntervalMs: 100,
      componentName: "AdobeDataLayer - Layout",
      signal: abortController.signal,
      // Layout polling depends on Sitecore fetch succeeding; on localhost the
      // Sitecore fetch is CORS-blocked, so this timeout is expected.
      quietOnLocalhost: true,
    })
      .then(() => layoutPromise.resolve())
      .catch((error) => {
        layoutPromise.resolve();
        trackError(error);
      });

    waitForCohesion({
      timeoutMs: 10000,
      pollIntervalMs: 100,
      componentName: "AdobeDataLayer - Cohesion",
      signal: abortController.signal,
      quietOnLocalhost: true,
    })
      .then(() => cohesionPromise.resolve())
      .catch((error) => {
        cohesionPromise.resolve();
        trackError(error);
      });

    return () => {
      abortController.abort();
    };
  }, []);

  useEffect(() => {
    if (previousScreenNameRef.current === screenName) {
      return;
    }

    previousScreenNameRef.current = screenName;

    if (!isTrackingReadyRef.current) {
      return;
    }

    document.dispatchEvent(
      new CustomEvent("c-tracking-log-page-start", {
        bubbles: true,
      })
    );
    handlePageView(screenName);
  }, [screenName]);

  return (
    <>
      {/* Data Layer CDN Script */}
      {/* eslint-disable-next-line @next/next/no-before-interactive-script-outside-document */}
      <Script src={url} strategy="beforeInteractive" />
      {/* Schema Version */}
      <script
        type="tracking-data-digitalData"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            schemaVersion: "0.21.1",
          }),
        }}
      />
      <script
        type="tracking-data-page"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            category: {
              businessType: "resi",
              designType: "rv",
              primaryCategory,
              siteType: "sales",
            },
            codebase: {
              name: "rv_comcast_landing_page",
            },
            pageInfo: {
              language: "en",
              ...(pageName ? { pageName } : {}),
              screenName,
            },
            affiliate: {
              channel: "web",
              name: "rv_comcast",
            },
          }),
        }}
      />
    </>
  );
}
